import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";

const PERM_GROUPS = {
  "Auth": ["auth.login"],
  "Chat": ["chat.use", "chat.use_voice"],
  "Knowledge": ["knowledge.read", "knowledge.read_all", "knowledge.create", "knowledge.update", "knowledge.delete", "knowledge.manage_access"],
  "Users": ["user.manage"],
  "Roles": ["role.manage"],
  "Audit": ["audit.read"],
  "System": ["system.manage", "voice.manage"],
};

function Modal({ title, onClose, children, maxWidth = "max-w-lg" }) {
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidth} my-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:border-[#10a37f] focus:ring-2 focus:ring-[#10a37f]/10 outline-none transition-all bg-white disabled:bg-gray-50";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function RoleFormModal({ title, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { code: "", name: "", description: "", is_active: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (initial?.id) {
        await api.updateRole(initial.id, {
          name: form.name,
          description: form.description || null,
          is_active: form.is_active,
        });
      } else {
        await api.createRole({
          code: form.code,
          name: form.name,
          description: form.description || null,
          is_active: form.is_active,
        });
      }
      onSaved();
    } catch (err) {
      setError(err.message || "Gagal menyimpan role");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
        {!initial?.id && (
          <Field label="Kode Role">
            <input
              className={inputCls}
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "_") })}
              placeholder="CONTOH_ROLE"
              required
            />
            <p className="text-xs text-gray-400 mt-1">Huruf kapital dan underscore saja, tidak bisa diubah setelah dibuat.</p>
          </Field>
        )}
        {initial?.id && (
          <Field label="Kode Role">
            <input className={inputCls} value={initial.code} disabled />
          </Field>
        )}
        <Field label="Nama Role">
          <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </Field>
        <Field label="Deskripsi">
          <textarea className={inputCls} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="opsional" />
        </Field>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 rounded accent-[#10a37f]" />
          <span className="text-sm font-medium text-gray-700">Role Aktif</span>
        </label>
        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">Batal</button>
          <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white text-sm font-semibold transition-colors disabled:opacity-50">
            {loading ? "Menyimpan..." : (initial?.id ? "Simpan Perubahan" : "Buat Role")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PermissionsPanel({ role, permissions, onSaved }) {
  const [rolePerms, setRolePerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  // Build a map of permission code → id from the global permissions list
  const permMap = Object.fromEntries(permissions.map((p) => [p.code, p]));

  const loadRolePerms = useCallback(async () => {
    setLoading(true);
    setStatus("");
    try {
      const perms = await api.getRolePermissions(role.id);
      setRolePerms(Array.isArray(perms) ? perms.map((p) => p.code || p) : []);
    } catch (err) {
      setStatus("Gagal memuat permissions role: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  }, [role.id]);

  useEffect(() => {
    loadRolePerms();
  }, [loadRolePerms]);

  function toggle(permCode) {
    setRolePerms((prev) =>
      prev.includes(permCode) ? prev.filter((p) => p !== permCode) : [...prev, permCode]
    );
  }

  async function save() {
    setSaving(true);
    setStatus("");
    try {
      const permIds = rolePerms
        .map((code) => permMap[code]?.id)
        .filter(Boolean);
      await api.setRolePermissions(role.id, { permission_ids: permIds });
      setStatus("Permissions berhasil disimpan!");
      onSaved();
      setTimeout(() => setStatus(""), 3000);
    } catch (err) {
      setStatus("Gagal menyimpan: " + (err.message || ""));
    } finally {
      setSaving(false);
    }
  }

  function selectAll(group) {
    const codes = PERM_GROUPS[group] || [];
    setRolePerms((prev) => {
      const set = new Set(prev);
      codes.forEach((c) => set.add(c));
      return [...set];
    });
  }

  function deselectAll(group) {
    const codes = new Set(PERM_GROUPS[group] || []);
    setRolePerms((prev) => prev.filter((p) => !codes.has(p)));
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className="font-bold text-gray-900">Permissions</h3>
          <p className="text-xs text-gray-400 mt-0.5">Role: <span className="font-semibold text-gray-700">{role.name}</span></p>
        </div>
        <button
          onClick={save}
          disabled={saving || loading}
          className="w-full sm:w-auto justify-center px-4 py-2 rounded-xl bg-[#10a37f] hover:bg-[#0e8f6c] text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-[16px]">{saving ? "hourglass_empty" : "save"}</span>
          {saving ? "Menyimpan..." : "Simpan"}
        </button>
      </div>

      {status && (
        <div className={`mb-4 px-4 py-2.5 rounded-xl text-sm font-medium ${status.includes("berhasil") ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {status}
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Memuat...</div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 sm:space-y-4 pr-1">
          {Object.entries(PERM_GROUPS).map(([group, codes]) => {
            const groupPerms = codes.filter((c) => permMap[c]);
            if (groupPerms.length === 0) return null;
            const allChecked = groupPerms.every((c) => rolePerms.includes(c));
            return (
              <div key={group} className="bg-gray-50 rounded-xl border border-gray-100 p-3 sm:p-4 min-w-0">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">{group}</p>
                  <button
                    type="button"
                    onClick={() => allChecked ? deselectAll(group) : selectAll(group)}
                    className="text-xs text-[#10a37f] font-semibold hover:underline whitespace-nowrap"
                  >
                    {allChecked ? "Unselect all" : "Select all"}
                  </button>
                </div>
                <div className="space-y-2">
                  {groupPerms.map((code) => {
                    const perm = permMap[code];
                    return (
                      <label key={code} className="flex items-start gap-3 cursor-pointer group min-w-0">
                        <input
                          type="checkbox"
                          checked={rolePerms.includes(code)}
                          onChange={() => toggle(code)}
                          className="w-4 h-4 rounded accent-[#10a37f] flex-shrink-0 mt-0.5"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 group-hover:text-[#10a37f] transition-colors break-words [overflow-wrap:anywhere]">{code}</p>
                          {perm?.description && <p className="text-xs text-gray-400 break-words [overflow-wrap:anywhere]">{perm.description}</p>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function RolesView() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [rolesData, permsData] = await Promise.all([api.listRoles(), api.listPermissions()]);
      const rolesArr = Array.isArray(rolesData) ? rolesData : [];
      setRoles(rolesArr);
      setPermissions(Array.isArray(permsData) ? permsData : []);
      if (selectedRole) {
        const updated = rolesArr.find((r) => r.id === selectedRole.id);
        if (updated) setSelectedRole(updated);
      }
    } catch (err) {
      setError(err.message || "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, [selectedRole]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  function onSaved() {
    load();
    setShowCreate(false);
    setEditingRole(null);
  }

  return (
    <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-center justify-between min-h-16 py-3 pl-14 pr-4 lg:px-6 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-white text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>admin_panel_settings</span>
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-gray-900 text-base md:text-lg leading-tight truncate">Roles & Permissions</h1>
              <p className="text-xs text-gray-400 hidden sm:block">Kelola role dan izin akses sistem</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white text-sm font-semibold transition-colors flex-shrink-0"
          >
            <span className="material-symbols-outlined text-[18px]">add_moderator</span>
            <span className="hidden sm:inline">Tambah Role</span>
          </button>
        </div>
      </header>

      <div className="flex-1 w-full min-w-0 p-3 sm:p-4 md:p-6">
        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] gap-4 md:gap-6 items-start min-w-0">
          {/* Roles List */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden min-w-0">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
              <h2 className="font-bold text-gray-800 text-sm">Daftar Role</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center text-gray-400 text-sm">Memuat...</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {roles.length === 0 && (
                  <p className="p-6 text-sm text-gray-400 text-center">Belum ada role.</p>
                )}
                {roles.map((role) => (
                  <div
                    key={role.id}
                    className={`flex items-center justify-between px-5 py-4 cursor-pointer transition-colors ${
                      selectedRole?.id === role.id ? "bg-[#10a37f]/5 border-l-2 border-[#10a37f]" : "hover:bg-gray-50"
                    }`}
                    onClick={() => setSelectedRole(role)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 text-sm truncate">{role.name}</p>
                        {!role.is_active && (
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded font-semibold">nonaktif</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{role.code}</p>
                      {role.description && <p className="text-xs text-gray-400 truncate mt-0.5">{role.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingRole(role); }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                        title="Edit Role"
                      >
                        <span className="material-symbols-outlined text-[16px]">edit</span>
                      </button>
                      <span className={`material-symbols-outlined text-[18px] ${selectedRole?.id === role.id ? "text-[#10a37f]" : "text-gray-200"}`}>chevron_right</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Permissions Panel */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 min-h-[400px] flex flex-col min-w-0">
            {!selectedRole ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 py-12">
                <span className="material-symbols-outlined text-5xl mb-3 text-gray-200">admin_panel_settings</span>
                <p className="font-medium text-gray-500">Pilih role untuk kelola permissions</p>
                <p className="text-sm mt-1">Klik salah satu role di sebelah kiri</p>
              </div>
            ) : (
              <PermissionsPanel
                key={selectedRole.id}
                role={selectedRole}
                permissions={permissions}
                onSaved={load}
              />
            )}
          </div>
        </div>
      </div>

      {showCreate && (
        <RoleFormModal
          title="Buat Role Baru"
          onClose={() => setShowCreate(false)}
          onSaved={onSaved}
        />
      )}
      {editingRole && (
        <RoleFormModal
          title={`Edit Role — ${editingRole.name}`}
          initial={editingRole}
          onClose={() => setEditingRole(null)}
          onSaved={onSaved}
        />
      )}
    </main>
  );
}
